import { useState } from 'react';
import { products } from '../../../mocks/products';
import { useInView } from '../../../hooks/useInView';

type Category = 'Todos' | 'Skin Care' | 'Body Care' | 'Higiene Íntima' | 'Home Care';
const categories: Category[] = ['Todos', 'Skin Care', 'Body Care', 'Higiene Íntima', 'Home Care'];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-xs ${
            star <= Math.floor(rating) ? 'ri-star-fill text-[#0F7C7A]' : 'ri-star-line text-[#7FCECC]'
          }`}
        />
      ))}
    </div>
  );
}

export default function ProductsSection() {
  const [activeCategory, setActiveCategory] = useState<Category>('Todos');
  const { ref, inView } = useInView();

  const filtered = activeCategory === 'Todos'
    ? products
    : products.filter((p) => p.category === activeCategory);

  return (
    <section id="produtos" className="py-24 bg-[#F5F5F5]">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className={`text-center mb-14 ${inView ? 'animate-fade-in-up' : 'opacity-0-init'}`}>
          <p className="text-[#0F7C7A] text-sm font-semibold tracking-[0.2em] uppercase mb-3">
            Nossos Produtos
          </p>
          <h2 className="font-display text-4xl lg:text-5xl text-[#1A1A1A] font-bold mb-4">
            Descubra os queridinhos do nosso catálogo
          </h2>
          <p className="text-[#555555] text-base max-w-lg mx-auto leading-relaxed">
            Cada produto é formulado com os melhores ingredientes naturais para oferecer resultados visíveis e duradouros.
          </p>
        </div>

        {/* Filter */}
        <div className={`flex justify-center mb-10 ${inView ? 'animate-fade-in-up delay-200' : 'opacity-0-init'}`}>
          <div className="inline-flex bg-[#E0F5F5] rounded-full p-1 gap-1 flex-wrap justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#0F7C7A] text-white'
                    : 'text-[#555555] hover:text-[#0F7C7A]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
          {filtered.map((product, idx) => (
            <div
              key={product.id}
              className={`group bg-white rounded-2xl overflow-hidden border border-[#DFF0F0] hover:border-[#0F7C7A]/40 transition-all duration-400 cursor-pointer ${
                inView ? `animate-scale-in delay-${Math.min(idx * 100, 500)}` : 'opacity-0-init'
              }`}
            >
              {/* Image */}
              <div className="relative bg-white flex items-center justify-center overflow-hidden p-6" style={{ height: '280px' }}>
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full group-hover:scale-105 transition-transform duration-500"
                  style={{ objectFit: 'contain', objectPosition: 'center' }}
                />
                {product.badge && (
                  <span className="absolute top-3 left-3 bg-[#0A5F5C] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {product.badge}
                  </span>
                )}
                <button className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-colors cursor-pointer">
                  <i className="ri-heart-line text-[#0F7C7A] text-sm" />
                </button>
              </div>

              {/* Info */}
              <div className="p-5">
                <span className="text-[#0F7C7A] text-xs font-semibold tracking-wider uppercase">
                  {product.category}
                </span>
                <h3 className="font-display text-base font-bold text-[#2C2C2C] mt-1 leading-snug">
                  {product.name}
                </h3>
                {product.subtitle && (
                  <p className="text-[#0A5F5C] text-xs font-medium mt-0.5 mb-2">
                    {product.subtitle}
                  </p>
                )}
                {product.description && (
                  <p className="text-[#7A6860] text-xs leading-relaxed mb-3 line-clamp-2">
                    {product.description}
                  </p>
                )}
                <div className="flex items-center gap-2 mb-4">
                  <StarRating rating={product.rating} />
                  <span className="text-[#555555] text-xs">({product.reviews})</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xl font-bold text-[#1A1A1A]">
                      R$ {product.price.toFixed(2).replace('.', ',')}
                    </span>
                    <span className="text-[#999999] text-sm line-through ml-2">
                      R$ {product.originalPrice.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                  <button className="bg-[#0F7C7A] hover:bg-[#0A5F5C] text-white w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer">
                    <i className="ri-shopping-bag-line text-sm" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-14 ${inView ? 'animate-fade-in-up delay-600' : 'opacity-0-init'}`}>
          <button
            onClick={() => document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' })}
            className="border-2 border-[#0F7C7A] text-[#0F7C7A] hover:bg-[#0F7C7A] hover:text-white font-semibold px-10 py-3.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Ver Catálogo Completo
          </button>
        </div>
      </div>
    </section>
  );
}
